import { useCallback, useState } from 'react';
import { djs } from '@duke-hq/djs';
import { SortOrder } from '@duke-hq/lib/pagination';
import type { DateRangeKey } from '@duke-hq/svc-api/provider/trpc/routers/tenant/groupRewards/utils';

export type SortFilters = {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  dateRange: DateRangeKey | undefined;
  month?: number;
  year?: number;
};

export function useMonthNavigation(defaultOrderBy: string) {
  const [currentMonth, setCurrentMonth] = useState(() => djs());
  const [filters, setFilters] = useState<SortFilters>(() => {
    const now = djs();
    return {
      orderBy: defaultOrderBy,
      sort: SortOrder.DESC,
      dateRange: undefined,
      month: now.month() + 1,
      year: now.year(),
    };
  });

  const today = djs();

  const updateFilters = useCallback(
    (prevFilters: SortFilters, newFilters: Partial<SortFilters>): SortFilters => {
      return {
        ...prevFilters,
        ...newFilters,
        orderBy: newFilters.orderBy ?? prevFilters.orderBy ?? defaultOrderBy,
        sort: newFilters.sort ?? prevFilters.sort ?? SortOrder.DESC,
      };
    },
    [defaultOrderBy],
  );

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      setFilters((prevFilters) => updateFilters(prevFilters, newFilters));
    },
    [updateFilters],
  );

  const goToPreviousMonth = useCallback(() => {
    setCurrentMonth((prev) => {
      const newMonth = prev.subtract(1, 'month');
      handleFilterChange({
        month: newMonth.month() + 1,
        year: newMonth.year(),
      });
      return newMonth;
    });
  }, [handleFilterChange]);

  const goToNextMonth = useCallback(() => {
    setCurrentMonth((prev) => {
      const newMonth = prev.add(1, 'month');
      handleFilterChange({
        month: newMonth.month() + 1,
        year: newMonth.year(),
      });
      return newMonth;
    });
  }, [handleFilterChange]);

  const isCurrentMonth = currentMonth.isSame(today, 'month');
  const canGoNext = !isCurrentMonth;
  const canGoPrev = true; // Allow going back to previous months
  const displayMonth = currentMonth.format('MMMM YYYY');

  return {
    currentMonth,
    filters,
    displayMonth,
    canGoNext,
    canGoPrev,
    goToPreviousMonth,
    goToNextMonth,
    handleFilterChange,
  };
}
